
import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    CardMedia,
    Divider,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { getUserDetails } from "../../Apis/UsersApi";

const UserDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchUser = async () => {
            try {
                setLoading(true);
                const data = await getUserDetails(id);
                const userData = data?.userDetails;
                setUser(userData || null);
                setBookings(userData?.bookings || []);
            } catch (err) {
                setError("Failed to fetch user details.");
            } finally {
                setLoading(false);
            }
        };
        fetchUser();
    }, [id]);

    if (loading)
        return (
            <Box sx={{ textAlign: "center", mt: 5 }}>
                <CircularProgress />
            </Box>
        );

    if (error)
        return (
            <Box sx={{ textAlign: "center", mt: 5 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );

    if (!user)
        return (
            <Typography sx={{ textAlign: "center", mt: 5 }}>
                No user found.
            </Typography>
        );

    return (
        <Box sx={{ width: "95%", maxWidth: 1000, margin: "auto", mt: 5, mb: 5 }}>
            <Button
                variant="outlined"
                sx={{ mb: 3 }}
                onClick={() => navigate("/users")}
            >
                Back to Users List
            </Button>

            {/* Profile Card */}
            <Card sx={{ boxShadow: 3, borderRadius: 3, mb: 4 }}>
                <CardContent>
                    <Typography variant="h5" fontWeight="bold" mb={2}>
                        User Profile
                    </Typography>
                    <Typography><strong>Name:</strong> {user.name || "N/A"}</Typography>
                    <Typography><strong>Email:</strong> {user.email || "N/A"}</Typography>
                    <Typography><strong>Mobile:</strong> {user.mobile || "N/A"}</Typography>

                    {/* User Addresses */}
                    {user.user_addresses?.length > 0 ? (
                        <Box mt={2}>
                            <Typography variant="subtitle1" fontWeight="bold">Addresses:</Typography>
                            {user.user_addresses.map((addr, idx) => (
                                <Box key={idx} sx={{ ml: 1, mt: 1 }}>
                                    {Object.entries(addr).map(([key, value]) => (
                                        <Typography key={key}>
                                            <strong>{key.charAt(0).toUpperCase() + key.slice(1)}:</strong> {value || "-"}
                                        </Typography>
                                    ))}
                                    {idx < user.user_addresses.length - 1 && <Divider sx={{ my: 1 }} />}
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <Typography mt={2}>No addresses found.</Typography>
                    )}

                    {/* User Image */}
                    {user.imageUrl && (
                        <CardMedia
                            component="img"
                            height="150"
                            image={user.imageUrl}
                            alt={user.name}
                            sx={{ objectFit: "contain", mt: 2, borderRadius: 1 }}
                        />
                    )}
                </CardContent>
            </Card>

            {/* Bookings */}
            <Card sx={{ boxShadow: 3, borderRadius: 3 }}>
                <CardContent>
                    <Typography variant="h5" fontWeight="bold" mb={2}>
                        Bookings ({bookings.length})
                    </Typography>

                    {bookings.length === 0 ? (
                        <Typography>No bookings found.</Typography>
                    ) : (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                            {bookings.map((b) => (
                                <Card key={b.bookingId} sx={{ boxShadow: 3, borderRadius: 3 }}>
                                    <CardContent>
                                        {/* Booking Info */}
                                        <Typography variant="h6" fontWeight="bold" mb={1}>
                                            Booking ID: {b.bookingId}
                                        </Typography>
                                        <Typography><strong>Type:</strong> {b.booking_type}</Typography>
                                        <Typography><strong>Status:</strong> {b.status}</Typography>
                                        <Typography><strong>Total Amount:</strong> ₹{b.total_amout}</Typography>
                                        <Typography><strong>Delivery Price:</strong> ₹{b.delivery_price}</Typography>
                                        <Typography><strong>Handling Price:</strong> ₹{b.handlingPrice}</Typography>
                                        <Typography><strong>Membership Discount:</strong> ₹{b.membership_discount}</Typography>
                                        <Typography><strong>Created At:</strong> {b.createdAt}</Typography>

                                        {/* Store Details */}
                                        {b.store ? (
                                            <Box mt={1}>
                                                <Typography variant="subtitle1" fontWeight="bold">Store Details:</Typography>
                                                <Typography><strong>Name:</strong> {b.store.name}</Typography>
                                            </Box>
                                        ) : (
                                            <Typography mt={1}>No store info.</Typography>
                                        )}

                                        {/* Products */}
                                        {b.booking_items?.length > 0 ? (
                                            <Box mt={2}>
                                                <Typography variant="subtitle1" fontWeight="bold">Products:</Typography>
                                                {b.booking_items.map((item) => (
                                                    <Box key={item.id} sx={{ ml: 1, mt: 1, p: 1, borderBottom: "1px solid #eee" }}>
                                                        <Typography><strong>Name:</strong> {item.product?.name || "-"}</Typography>
                                                        <Typography><strong>Price:</strong> ₹{item.price || "0.00"}</Typography>
                                                        <Typography><strong>Quantity:</strong> {item.quantity || 0}</Typography>
                                                        <Typography><strong>Weight:</strong> {item.product_weight || 0} kg</Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        ) : (
                                            <Typography mt={2}>No products found.</Typography>
                                        )}

                                        {/* Booking Time Slots */}
                                        {b["booking_time-slots"]?.length > 0 ? (
                                            <Box mt={2}>
                                                <Typography variant="subtitle1" fontWeight="bold">Booking Time Slots:</Typography>
                                                {b["booking_time-slots"].map((slot) => (
                                                    <Box key={slot.id} sx={{ ml: 1, mt: 1 }}>
                                                        <Typography><strong>Date:</strong> {slot.bookingDate || "-"}</Typography>
                                                        <Typography><strong>Time:</strong> {slot.bookingTime || "-"}</Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        ) : (
                                            <Typography mt={2}>No time slots assigned.</Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default UserDetails;


